import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import { Star, Plus, X, CheckCircle2, RefreshCw, AlertCircle, Eye } from "lucide-react";
import { formatDate } from "@/lib/dates";

function dueUrgency(dueDate, stage) {
  if (!dueDate || stage === "signed_off" || stage === "cancelled") return null;
  const d = new Date(dueDate);
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const days = Math.round((d - t) / 86400000);
  if (days < 0)  return { cls: "bg-[#fde8e3] text-[#9b2c1b]",  label: `${Math.abs(days)}d overdue`, level: "overdue" };
  if (days < 7)  return { cls: "bg-[#fde8e3] text-[#9b2c1b]",  label: `${days}d left`,            level: "<7" };
  if (days < 14) return { cls: "bg-[#fdf3ec] text-[#a3551c]",  label: `${days}d left`,            level: "<14" };
  if (days < 30) return { cls: "bg-[#fef9e7] text-[#7a5300]",  label: `${days}d left`,            level: "<30" };
  return null;
}

const STAGE_PILL = {
  scheduled:            { cls: "pill-info",    label: "Scheduled" },
  employee_in_progress: { cls: "pill-warning", label: "Awaiting employee" },
  manager_in_progress:  { cls: "pill-warning", label: "Awaiting manager" },
  hr_review:            { cls: "pill-warning", label: "HR review" },
  signed_off:           { cls: "pill-success", label: "Signed off" },
  cancelled:            { cls: "pill-danger",  label: "Cancelled" },
};

export default function AppraisalsPage() {
  const [tab, setTab] = useState("all");
  const [rows, setRows] = useState([]);
  const [dash, setDash] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    try {
      const [{ data }, { data: d }] = await Promise.all([
        api.get(tab === "all" ? "/appraisals" : `/appraisals?stage=${tab}`),
        api.get("/appraisals/dashboard"),
      ]);
      setRows(data || []);
      setDash(d);
    } catch (e) { toast.error("Failed to load appraisals"); }
  };
  useEffect(() => { load(); }, [tab]);

  return (
    <div className="fade-up">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-eyebrow mb-2">Phase 3a</div>
          <h1 className="font-display text-4xl font-bold flex items-center gap-3"><Star className="w-8 h-8" /> Performance Appraisals</h1>
          <p className="text-[var(--cc-text-soft)] mt-1">Annual reviews and 6-month probation checks. HR schedules → employee self-rates → line manager rates → HR signs off.</p>
        </div>
        <button className="btn-primary inline-flex items-center gap-1.5" onClick={() => setShowSchedule(true)} data-testid="schedule-appraisal-button">
          <Plus className="w-4 h-4" /> Schedule appraisal
        </button>
      </div>

      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          {Object.entries(STAGE_PILL).map(([stage, sp]) => (
            <div key={stage} className="cc-card p-3" data-testid={`appraisal-summary-${stage}`}>
              <div className="label-eyebrow mb-1 text-[10px]">{sp.label}</div>
              <div className="font-display text-2xl font-bold">{dash.by_stage?.[stage] || 0}</div>
            </div>
          ))}
        </div>
      )}

      {dash?.overdue?.length > 0 && (
        <div className="cc-card p-4 mb-5 bg-[#fdf3ec] border-l-4 border-[var(--cc-accent)]" data-testid="appraisal-overdue-banner">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-[var(--cc-accent)] shrink-0 mt-0.5" />
            <div>
              <div className="font-bold mb-1">{dash.overdue.length} overdue appraisal{dash.overdue.length === 1 ? "" : "s"}</div>
              <div className="text-xs">{dash.overdue.slice(0, 5).map((o) => o.employee_name).join(", ")}{dash.overdue.length > 5 ? ` +${dash.overdue.length - 5} more` : ""}</div>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-1 mb-4 overflow-x-auto">
        {["all", "scheduled", "employee_in_progress", "manager_in_progress", "hr_review", "signed_off"].map((t) => (
          <button
            key={t}
            className={`px-3 py-1.5 text-xs rounded-full whitespace-nowrap ${tab === t ? "bg-[var(--cc-sage)] text-white" : "bg-[var(--cc-surface)] hover:bg-[var(--cc-surface-hover)]"}`}
            onClick={() => setTab(t)}
            data-testid={`appraisal-filter-${t}`}
          >{t.replace(/_/g, " ")}</button>
        ))}
      </div>

      <div className="cc-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--cc-surface)]">
            <tr className="text-left">
              <th className="px-4 py-2 label-eyebrow">Employee</th>
              <th className="px-4 py-2 label-eyebrow">Cycle</th>
              <th className="px-4 py-2 label-eyebrow">Due</th>
              <th className="px-4 py-2 label-eyebrow">Line manager</th>
              <th className="px-4 py-2 label-eyebrow">Stage</th>
              <th className="px-4 py-2 label-eyebrow"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan="6" className="text-center py-10 text-[var(--cc-text-soft)]" data-testid="empty-appraisals">No appraisals yet.</td></tr>
            )}
            {rows.map((a) => {
              const sp = STAGE_PILL[a.stage] || STAGE_PILL.scheduled;
              const urg = dueUrgency(a.due_date, a.stage);
              return (
                <tr key={a.id} className="border-t border-[var(--cc-border)]" data-testid={`appraisal-row-${a.id}`}>
                  <td className="px-4 py-2 font-medium">{a.employee_name}</td>
                  <td className="px-4 py-2 text-xs">{a.cycle === "annual" ? "Annual" : "6-month probation"}</td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <span>{formatDate(a.due_date)}</span>
                      {urg && (
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${urg.cls}`} data-testid={`due-urgency-${a.id}-${urg.level}`}>
                          {urg.label}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-xs">{a.line_manager_name || <span className="italic text-[var(--cc-text-soft)]">unassigned</span>}</td>
                  <td className="px-4 py-2"><span className={`pill ${sp.cls}`}>{sp.label}</span></td>
                  <td className="px-4 py-2">
                    <button className="btn-ghost text-xs inline-flex items-center gap-1" onClick={() => setOpenId(a.id)} data-testid={`open-appraisal-${a.id}`}>
                      <Eye className="w-3.5 h-3.5" /> Open
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showSchedule && <ScheduleModal onClose={() => setShowSchedule(false)} onSaved={() => { setShowSchedule(false); load(); }} />}
      {openId && <AppraisalDetailModal id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}

function ScheduleModal({ onClose, onSaved }) {
  const [emps, setEmps] = useState([]);
  const [form, setForm] = useState({ employee_id: "", cycle: "annual", due_date: "", line_manager_user_id: "", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/employees").then((r) => setEmps(r.data || [])); }, []);

  const submit = async () => {
    if (!form.employee_id) return toast.error("Please select an employee");
    if (!form.due_date) return toast.error("Please pick a due date");
    setBusy(true);
    try {
      const payload = { ...form };
      // Don't send empty strings for optional fields — Pydantic prefers null/absent
      if (!payload.line_manager_user_id) delete payload.line_manager_user_id;
      if (!payload.notes) delete payload.notes;
      await api.post("/appraisals", payload);
      toast.success("Appraisal scheduled");
      onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join("; ") : (detail || "Failed to schedule");
      toast.error(msg);
    }
    finally { setBusy(false); }
  };
  const selectedEmp = emps.find((e) => e.id === form.employee_id);

  return (
    <Modal onClose={onClose} widthClass="max-w-lg" testId="schedule-appraisal-modal">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold">Schedule appraisal</h2>
        <button className="btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>
      <div className="space-y-3">
        <div>
          <label className="label-eyebrow block mb-1">Employee *</label>
          <select className="cc-select" value={form.employee_id} onChange={(e) => setForm({...form, employee_id: e.target.value})} data-testid="appraisal-employee-select">
            <option value="">— Select employee —</option>
            {emps.map((e) => <option key={e.id} value={e.id}>{e.full_name}{e.line_manager_user_id ? " ✓ has manager" : ""}</option>)}
          </select>
          {selectedEmp && !selectedEmp.line_manager_user_id && (
            <div className="text-[11px] text-[var(--cc-accent)] mt-1">No line manager assigned to this employee. Set one in their Profile → Employment section, or pick one below for this appraisal.</div>
          )}
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Cycle *</label>
          <select className="cc-select" value={form.cycle} onChange={(e) => setForm({...form, cycle: e.target.value})} data-testid="appraisal-cycle-select">
            <option value="annual">Annual review</option>
            <option value="probation_6m">6-month probation check</option>
          </select>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Due date *</label>
          <input type="date" className="cc-input" value={form.due_date} onChange={(e) => setForm({...form, due_date: e.target.value})} data-testid="appraisal-due-date" />
        </div>
        <LineManagerSelect value={form.line_manager_user_id} onChange={(v) => setForm({...form, line_manager_user_id: v})} />
        <div>
          <label className="label-eyebrow block mb-1">Notes (optional)</label>
          <textarea className="cc-input" rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} />
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={submit} disabled={busy} data-testid="appraisal-submit">{busy ? "Scheduling…" : "Schedule"}</button>
      </div>
    </Modal>
  );
}

function LineManagerSelect({ value, onChange }) {
  const [opts, setOpts] = useState([]);
  useEffect(() => { api.get("/employees/line-manager-options").then((r) => setOpts(r.data || [])).catch(() => {}); }, []);
  return (
    <div>
      <label className="label-eyebrow block mb-1">Line manager (override)</label>
      <select className="cc-select" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid="appraisal-line-manager-select">
        <option value="">— Use employee's saved line manager —</option>
        {opts.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.role})</option>)}
      </select>
    </div>
  );
}

function AppraisalDetailModal({ id, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [competencies, setCompetencies] = useState([]);
  const [signOff, setSignOff] = useState({ decision: "approve", summary: "", requested_changes: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [{ data: a }, { data: c }] = await Promise.all([
      api.get(`/appraisals/${id}`),
      api.get("/appraisals/competencies"),
    ]);
    setData(a);
    setCompetencies(c.competencies);
  };
  useEffect(() => { load(); }, [id]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/appraisals/${id}/signoff`, signOff);
      toast.success(signOff.decision === "approve" ? "Appraisal signed off" : "Revision requested");
      onChanged();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const canSignOff = data.stage === "hr_review";
  const canSubmitManager = !data.manager_scorecard && ["scheduled", "employee_in_progress", "manager_in_progress"].includes(data.stage);

  return (
    <Modal onClose={onClose} widthClass="max-w-3xl" testId="appraisal-detail-modal">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-display text-xl font-bold">{data.employee_name}</h2>
          <div className="text-xs text-[var(--cc-text-soft)]">{data.cycle === "annual" ? "Annual" : "6-month probation"} · Due {formatDate(data.due_date)} · <span className="font-semibold">{(STAGE_PILL[data.stage] || {}).label || data.stage}</span></div>
        </div>
        <button className="btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <ScoreCard title="Employee self-assessment" sc={data.employee_scorecard} competencies={competencies} testIdPrefix="employee" />
        <ScoreCard title={`Line manager (${data.line_manager_name || "unassigned"})`} sc={data.manager_scorecard} competencies={competencies} testIdPrefix="manager" />
      </div>

      {canSubmitManager && (
        <ManagerScorecardForm appraisalId={id} competencies={competencies} onSubmitted={() => { load(); onChanged(); }} />
      )}

      {data.hr_signoff && (
        <div className="cc-card p-4 mb-4 bg-[var(--cc-surface)] text-sm" data-testid="hr-signoff-card">
          <div className="label-eyebrow mb-1">HR sign-off</div>
          <div className="font-medium">{data.hr_signoff.decision} {data.hr_signoff.signed_by_name ? `by ${data.hr_signoff.signed_by_name}` : ""} {data.hr_signoff.signed_at ? `· ${data.hr_signoff.signed_at.slice(0,10)}` : ""}</div>
          {data.hr_signoff.summary && <div className="mt-2 text-xs">{data.hr_signoff.summary}</div>}
          {data.hr_signoff.requested_changes && <div className="mt-2 text-xs"><strong>Requested changes:</strong> {data.hr_signoff.requested_changes}</div>}
        </div>
      )}

      {canSignOff && (
        <div className="cc-card p-4">
          <div className="label-eyebrow mb-2">HR sign-off</div>
          <div className="flex gap-2 mb-3">
            {["approve", "request_revision"].map((d) => (
              <button
                key={d}
                className={`flex-1 py-2 rounded-lg text-sm font-medium ${signOff.decision === d ? (d === "approve" ? "bg-[var(--cc-sage)] text-white" : "bg-[var(--cc-accent)] text-white") : "bg-[var(--cc-surface)]"}`}
                onClick={() => setSignOff({...signOff, decision: d})}
                data-testid={`signoff-${d}`}
              >{d.replace("_", " ")}</button>
            ))}
          </div>
          {signOff.decision === "approve" ? (
            <textarea className="cc-input" rows={3} placeholder="Final summary…" value={signOff.summary} onChange={(e) => setSignOff({...signOff, summary: e.target.value})} data-testid="signoff-summary" />
          ) : (
            <textarea className="cc-input" rows={3} placeholder="What needs revision?" value={signOff.requested_changes} onChange={(e) => setSignOff({...signOff, requested_changes: e.target.value})} data-testid="signoff-changes" />
          )}
          <div className="mt-3 flex justify-end gap-2">
            <button className="btn-primary" onClick={submit} disabled={busy} data-testid="signoff-submit">{busy ? "Saving…" : "Submit"}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function ScoreCard({ title, sc, competencies, testIdPrefix }) {
  return (
    <div className="cc-card p-4" data-testid={`scorecard-${testIdPrefix}`}>
      <div className="label-eyebrow mb-2">{title}</div>
      {!sc ? (
        <div className="text-xs italic text-[var(--cc-text-soft)]">Not yet submitted</div>
      ) : (
        <>
          <div className="space-y-1 text-xs">
            {competencies.map((c) => (
              <div key={c.key} className="flex justify-between"><span>{c.label}</span><span className="font-mono font-bold">{sc.scores?.[c.key] || "-"}/5</span></div>
            ))}
            <div className="border-t border-[var(--cc-border)] mt-2 pt-2 flex justify-between font-semibold"><span>Average</span><span>{sc.average?.toFixed(2)}</span></div>
          </div>
          {sc.strengths && <div className="mt-3 text-xs"><strong>Strengths:</strong> {sc.strengths}</div>}
          {sc.areas_to_improve && <div className="mt-1 text-xs"><strong>Areas to improve:</strong> {sc.areas_to_improve}</div>}
          {sc.goals_next_period && <div className="mt-1 text-xs"><strong>Goals:</strong> {sc.goals_next_period}</div>}
          {sc.comments && <div className="mt-1 text-xs italic">{sc.comments}</div>}
          <div className="mt-2 text-[10px] text-[var(--cc-text-soft)]">Submitted {sc.submitted_at?.slice(0,10)} by {sc.submitted_by_name}</div>
        </>
      )}
    </div>
  );
}

function ManagerScorecardForm({ appraisalId, competencies, onSubmitted }) {
  const [form, setForm] = useState({ scores: {}, strengths: "", areas_to_improve: "", goals_next_period: "", comments: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const missing = competencies.find((c) => !form.scores[c.key]);
    if (missing) return toast.error(`Please rate "${missing.label}"`);
    setBusy(true);
    try {
      await api.post(`/appraisals/${appraisalId}/manager-scorecard`, form);
      toast.success("Manager scorecard submitted");
      onSubmitted();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="cc-card p-4 mb-4" data-testid="manager-scorecard-form">
      <div className="label-eyebrow mb-3">Line manager scorecard (you can submit on the manager's behalf, or have them log in)</div>
      <div className="space-y-2">
        {competencies.map((c) => (
          <div key={c.key} className="flex items-center justify-between gap-3">
            <div className="text-sm">{c.label}</div>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  className={`w-8 h-8 rounded-md text-xs font-bold ${form.scores[c.key] === n ? "bg-[var(--cc-sage)] text-white" : "bg-[var(--cc-surface)]"}`}
                  onClick={() => setForm({...form, scores: {...form.scores, [c.key]: n}})}
                  data-testid={`mgr-score-${c.key}-${n}`}
                >{n}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3 mt-4">
        <textarea className="cc-input" rows={2} placeholder="Strengths" value={form.strengths} onChange={(e) => setForm({...form, strengths: e.target.value})} data-testid="mgr-strengths" />
        <textarea className="cc-input" rows={2} placeholder="Areas to improve" value={form.areas_to_improve} onChange={(e) => setForm({...form, areas_to_improve: e.target.value})} data-testid="mgr-improve" />
        <textarea className="cc-input" rows={2} placeholder="Goals next period" value={form.goals_next_period} onChange={(e) => setForm({...form, goals_next_period: e.target.value})} data-testid="mgr-goals" />
        <textarea className="cc-input" rows={2} placeholder="Comments" value={form.comments} onChange={(e) => setForm({...form, comments: e.target.value})} data-testid="mgr-comments" />
      </div>
      <div className="mt-3 flex justify-end">
        <button className="btn-primary text-sm" onClick={submit} disabled={busy} data-testid="mgr-submit">{busy ? "Submitting…" : "Submit manager scorecard"}</button>
      </div>
    </div>
  );
}

